/**
 * Меню настроек Plyr раскрывается вверх (position: absolute; bottom: 100%) и не умеет
 * переворачиваться. Внутри контейнера с обрезкой — списка истории заявки или .table-responsive —
 * всё, что уходит выше его верхней границы, отрезается и доскроллить это нельзя.
 *
 * Миксин на время открытия переносит меню в position: fixed с координатами от шестерёнки,
 * так что обрезающий контейнер ему больше не мешает.
 *
 * Компонент должен повесить ref="plyr_player" на <VuePlyr>.
 *
 * CommonJS (module.exports) — default-import в компоненты, require в Node-тесте
 * (tests/js/plyrMenuPortal.test.js).
 */

// Отступ меню от кнопки — как штатный margin-bottom у .plyr__menu__container
const GAP = 10;
// Смещение правого края меню относительно кнопки — как штатный right: -3px
const RIGHT_OFFSET = 3;
// Минимальный зазор до края экрана
const EDGE = 8;

module.exports = {
    mounted() {
        // Не в data: обсервер и флаг не должны попадать в реактивность Vue.
        this.plyr_menu_observer = null;
        this.plyr_menu_listening = false;

        // Для HTML5-медиа Plyr строит контролы синхронно в конструкторе, а дочерний
        // VuePlyr монтируется раньше родителя — меню на этот момент уже существует.
        let popup = this.plyrMenuPopup();
        if (!popup) return;

        let self = this;
        this.plyr_menu_observer = new MutationObserver(function () {
            if (popup.hidden) {
                self.resetPlyrMenu();
            } else {
                self.placePlyrMenu();
            }
        });
        this.plyr_menu_observer.observe(popup, {attributes: true, attributeFilter: ['hidden']});
    },
    beforeDestroy() {
        if (this.plyr_menu_observer) {
            this.plyr_menu_observer.disconnect();
            this.plyr_menu_observer = null;
        }
        this.resetPlyrMenu();
    },
    methods: {
        plyrInstance() {
            return (this.$refs.plyr_player && this.$refs.plyr_player.player) || null;
        },
        plyrMenuPopup() {
            let plyr = this.plyrInstance();
            return (plyr && plyr.elements.settings && plyr.elements.settings.popup) || null;
        },
        plyrSettingsButton() {
            let plyr = this.plyrInstance();
            return (plyr && plyr.elements.buttons && plyr.elements.buttons.settings) || null;
        },
        placePlyrMenu() {
            let popup = this.plyrMenuPopup();
            let button = this.plyrSettingsButton();
            if (!popup || !button) return;

            let rect = button.getBoundingClientRect();
            let space_above = rect.top;
            let space_below = window.innerHeight - rect.bottom;
            // Высоту меню замерить нельзя: при переходе в подменю «Speed» Plyr её анимирует.
            // Поэтому выбираем сторону по свободному месту, а не по размеру меню.
            let open_up = space_above >= space_below;

            popup.style.position = 'fixed';
            popup.style.margin = '0';
            popup.style.left = 'auto';
            popup.style.right = (window.innerWidth - rect.right - RIGHT_OFFSET) + 'px';
            // Якорь по нижнему краю: меню растёт вверх, и менять координаты при смене
            // высоты (главное меню → подменю скоростей) не нужно.
            popup.style.top = open_up ? 'auto' : (rect.bottom + GAP) + 'px';
            popup.style.bottom = open_up ? (window.innerHeight - rect.top + GAP) + 'px' : 'auto';
            popup.style.maxHeight = Math.max(0, (open_up ? space_above : space_below) - GAP - EDGE) + 'px';
            popup.style.overflowY = 'auto';
            popup.classList.toggle('plyr-menu-below', !open_up);

            // .plyr задан z-index: 0, то есть каждый плеер — свой контекст наложения,
            // и штатный z-index: 3 у меню работает только внутри него.
            let plyr = this.plyrInstance();
            if (plyr && plyr.elements.container) plyr.elements.container.style.zIndex = '3';

            this.startPlyrMenuTracking();
        },
        resetPlyrMenu() {
            this.stopPlyrMenuTracking();

            let popup = this.plyrMenuPopup();
            if (popup) {
                ['position', 'margin', 'left', 'right', 'top', 'bottom', 'maxHeight', 'overflowY']
                    .forEach(prop => {popup.style[prop] = '';});
                popup.classList.remove('plyr-menu-below');
            }

            let plyr = this.plyrInstance();
            if (plyr && plyr.elements.container) plyr.elements.container.style.zIndex = '';
        },
        startPlyrMenuTracking() {
            if (this.plyr_menu_listening) return;
            this.plyr_menu_listening = true;
            // capture: true — иначе не поймать прокрутку внутренних контейнеров
            // (список истории заявки, горизонтальная прокрутка .table-responsive)
            window.addEventListener('scroll', this.placePlyrMenu, true);
            window.addEventListener('resize', this.placePlyrMenu);
        },
        stopPlyrMenuTracking() {
            if (!this.plyr_menu_listening) return;
            this.plyr_menu_listening = false;
            window.removeEventListener('scroll', this.placePlyrMenu, true);
            window.removeEventListener('resize', this.placePlyrMenu);
        },
    },
}
